import { useState } from 'react';
import html2canvas, { Options } from 'html2canvas';

interface Html2CanvasOptions extends Partial<Options> {
  type?: string; // Image format, e.g., 'image/png'
  quality?: number; // Image quality (0 to 1 for JPEG/WEBP)
}

interface TakeScreenShotProps {
  nodes: HTMLElement[];
  options?: Html2CanvasOptions;
  cbOnComplete?: (image: string) => void;
}

interface TakeScreenShot {
  (props: TakeScreenShotProps): Promise<string | void>;
}

interface downloadScreenshotProps {
  imageToDownload: string | undefined;
  name?: string;
  extension?: string;
}

interface CreateFullPageSnapshotReturn {
  image: string | null;
  takeScreenShot: TakeScreenShot;
  error: Error | null;
  download: (props: downloadScreenshotProps) => void;
}

const useCreateFullPageSnapShot = (): CreateFullPageSnapshotReturn => {
  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const takeScreenShot: TakeScreenShot = async ({ nodes, options = {}, cbOnComplete }) => {
    if (!nodes || nodes.length === 0) {
      throw new Error('You should provide an array of valid HTML nodes.');
    }

    try {
      const canvases: HTMLCanvasElement[] = await Promise.all(
        nodes.filter(Boolean).map((node) => html2canvas(node, options).then((canvas) => canvas)),
      );

      // Combine the canvases into a single image with 5px padding between each node
      const totalHeight = canvases.reduce((sum, canvas) => sum + canvas.height + 5, -5);
      const maxWidth = Math.max(...canvases.map((canvas) => canvas.width));

      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = maxWidth;
      combinedCanvas.height = totalHeight;
      const combinedContext = combinedCanvas.getContext('2d');

      if (!combinedContext) {
        throw new Error('Failed to get 2D context');
      }

      let currentHeight = 0;
      canvases.forEach((canvas) => {
        combinedContext.drawImage(canvas, 0, currentHeight);
        currentHeight += canvas.height;
      });

      const base64Image = combinedCanvas.toDataURL(options.type || 'image/png', options.quality || 1.0);
      setImage(base64Image);
      if (cbOnComplete) {
        cbOnComplete(base64Image);
      }
      return base64Image;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const download = ({
    name = 'screenshot',
    extension = 'png',
    imageToDownload = undefined,
  }: downloadScreenshotProps) => {
    if (!imageToDownload) {
      throw new Error('No image to download');
    }

    const link = document.createElement('a');
    link.href = imageToDownload;
    link.download = `${name}.${extension}`;
    link.click();
  };

  return {
    image,
    takeScreenShot,
    error,
    download,
  };
};

export { useCreateFullPageSnapShot };
